import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Tag, Check, X, Pencil } from "lucide-react";

const AUDIENCES = [
  { v: "any", l: "Everyone" },
  { v: "new", l: "New buyers (0 orders)" },
  { v: "returning", l: "Returning buyers (1+ orders)" },
];

export default function AdminCoupons() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ code: "", percent: 10, audience: "any", label: "", active: true });
  const [editingCode, setEditingCode] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    api.get("/coupons/admin/all")
      .then(({ data }) => setItems(data))
      .catch((err) => toast.error(err.response?.data?.detail || "Load failed"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) return toast.error("Code required");
    try {
      await api.post("/coupons/admin", {
        code: form.code.trim(),
        percent: Number(form.percent),
        audience: form.audience,
        label: form.label || null,
        active: form.active,
      });
      toast.success("Coupon created");
      setForm({ code: "", percent: 10, audience: "any", label: "", active: true });
      setCreating(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    }
  };

  const startEdit = (c) => {
    setEditingCode(c.code);
    setEditForm({ percent: c.percent, audience: c.audience, label: c.label || "", active: c.active });
  };

  const saveEdit = async (code) => {
    try {
      await api.patch(`/coupons/admin/${encodeURIComponent(code)}`, {
        percent: Number(editForm.percent),
        audience: editForm.audience,
        label: editForm.label || null,
        active: editForm.active,
      });
      toast.success("Updated");
      setEditingCode(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const toggleActive = async (c) => {
    try {
      await api.patch(`/coupons/admin/${encodeURIComponent(c.code)}`, { active: !c.active });
      toast.success(!c.active ? "Enabled" : "Disabled");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const del = async (code) => {
    if (!window.confirm(`Delete coupon ${code}?`)) return;
    try {
      await api.delete(`/coupons/admin/${encodeURIComponent(code)}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-coupons">
      <button
        onClick={() => setCreating(!creating)}
        data-testid="admin-coupon-new"
        className="bg-black text-[#D4FF00] border-2 border-black brutal-shadow hover-nudge px-4 py-2 font-mono-b font-bold uppercase inline-flex items-center gap-2"
      >
        <Plus className="w-4 h-4" /> New Coupon
      </button>

      {creating && (
        <form onSubmit={submit} className="bg-white border-2 border-black brutal-shadow p-4 grid md:grid-cols-2 gap-3" data-testid="admin-coupon-form">
          <div>
            <label className="text-xs font-mono-b font-bold uppercase">CODE</label>
            <input
              data-testid="cf-code"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="e.g. SPRING25"
              className="brutal-input w-full mt-1 uppercase"
              required
            />
          </div>
          <div>
            <label className="text-xs font-mono-b font-bold uppercase">PERCENT OFF (1-90)</label>
            <input
              type="number"
              min="1"
              max="90"
              data-testid="cf-percent"
              value={form.percent}
              onChange={(e) => setForm({ ...form, percent: e.target.value })}
              className="brutal-input w-full mt-1"
              required
            />
          </div>
          <div>
            <label className="text-xs font-mono-b font-bold uppercase">AUDIENCE</label>
            <select
              data-testid="cf-audience"
              value={form.audience}
              onChange={(e) => setForm({ ...form, audience: e.target.value })}
              className="brutal-input w-full mt-1 uppercase"
            >
              {AUDIENCES.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono-b font-bold uppercase">LABEL (shown to customer)</label>
            <input
              data-testid="cf-label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. Spring 25% off"
              className="brutal-input w-full mt-1"
            />
          </div>
          <label className="flex items-center gap-2 font-mono-b text-sm md:col-span-2">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            <span>Active (visible & usable by shoppers)</span>
          </label>
          <button
            type="submit"
            data-testid="cf-submit"
            className="md:col-span-2 bg-[#D4FF00] border-2 border-black brutal-shadow hover-nudge py-2 font-mono-b font-bold uppercase"
          >
            CREATE
          </button>
        </form>
      )}

      {loading ? (
        <p className="font-mono-b p-6" data-testid="admin-coupons-loading">Loading...</p>
      ) : items.length === 0 ? (
        <p className="font-mono-b p-8 text-center border-2 border-dashed border-black">No coupons. Create one above.</p>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map((c) => (
            <div key={c.code} className="bg-white border-2 border-black brutal-shadow-sm p-4" data-testid={`admin-coupon-${c.code}`}>
              <div className="flex justify-between items-start gap-2 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    <p className="font-display text-2xl leading-none">{c.code}</p>
                  </div>
                  <p className="font-mono-b text-xs mt-1"><b>{c.percent}%</b> off · {AUDIENCES.find(a => a.v === c.audience)?.l || c.audience}</p>
                  {c.label && <p className="font-mono-b text-[11px] text-black/60 mt-1">{c.label}</p>}
                </div>
                <span
                  data-testid={`ac-status-${c.code}`}
                  className={`sticker text-[10px] ${c.active ? "" : "pink"}`}
                >
                  {c.active ? "ACTIVE" : "INACTIVE"}
                </span>
              </div>

              {editingCode === c.code ? (
                <div className="mt-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input type="number" min="1" max="90" value={editForm.percent} onChange={(e) => setEditForm({ ...editForm, percent: e.target.value })} className="brutal-input w-full text-sm" data-testid={`ac-edit-percent-${c.code}`} />
                    <select value={editForm.audience} onChange={(e) => setEditForm({ ...editForm, audience: e.target.value })} className="brutal-input w-full text-sm uppercase" data-testid={`ac-edit-audience-${c.code}`}>
                      {AUDIENCES.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
                    </select>
                  </div>
                  <input value={editForm.label} onChange={(e) => setEditForm({ ...editForm, label: e.target.value })} className="brutal-input w-full text-sm" placeholder="Label" data-testid={`ac-edit-label-${c.code}`} />
                  <label className="flex items-center gap-2 font-mono-b text-xs">
                    <input type="checkbox" checked={editForm.active} onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })} data-testid={`ac-edit-active-${c.code}`} />
                    Active
                  </label>
                  <div className="flex gap-2">
                    <button onClick={() => saveEdit(c.code)} data-testid={`ac-save-${c.code}`} className="flex-1 bg-[#D4FF00] border-2 border-black py-1 font-mono-b text-xs font-bold uppercase inline-flex items-center justify-center gap-1"><Check className="w-3 h-3" /> SAVE</button>
                    <button onClick={() => setEditingCode(null)} data-testid={`ac-cancel-${c.code}`} className="flex-1 bg-white border-2 border-black py-1 font-mono-b text-xs font-bold uppercase inline-flex items-center justify-center gap-1"><X className="w-3 h-3" /> CANCEL</button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 flex gap-2 flex-wrap">
                  <button onClick={() => startEdit(c)} data-testid={`ac-edit-${c.code}`} className="border-2 border-black bg-white px-2 py-1 font-mono-b text-[11px] font-bold uppercase inline-flex items-center gap-1 hover-nudge"><Pencil className="w-3 h-3" /> EDIT</button>
                  <button onClick={() => toggleActive(c)} data-testid={`ac-toggle-${c.code}`} className={`border-2 border-black px-2 py-1 font-mono-b text-[11px] font-bold uppercase hover-nudge ${c.active ? "bg-[#F4F4F0]" : "bg-[#D4FF00]"}`}>
                    {c.active ? "DISABLE" : "ENABLE"}
                  </button>
                  <button onClick={() => del(c.code)} data-testid={`ac-del-${c.code}`} className="border-2 border-black bg-[#FF007F] text-white px-2 py-1 font-mono-b text-[11px] font-bold uppercase inline-flex items-center gap-1 hover-nudge"><Trash2 className="w-3 h-3" /> DEL</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
